import React from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import { Option, Select } from 'carbon-react/lib/components/select';
import Pill from 'carbon-react/lib/components/pill';
import Profile from 'carbon-react/lib/components/profile';

import { ExplicitPermissionLevel } from '../../hooks/use-share-resource-form';

import * as styles from './share-resource-user-row.scss';

export type ShareResourceUserAccessLevel = ExplicitPermissionLevel | 'owner';

export interface ShareResourceUserRowProps {
  accessLevel: ShareResourceUserAccessLevel;
  avatar?: string | null;
  name: string;
  onAccessLevelChange?: (accessLevel: ExplicitPermissionLevel) => void;
  onRemove?: () => void;
}

export const ShareResourceUserRow = ({
  accessLevel,
  avatar,
  name,
  onAccessLevelChange,
  onRemove
}: ShareResourceUserRowProps) => {
  const intl = useIntl();
  const isOwner = accessLevel === 'owner';

  return (
    <li className={styles.item}>
      <Box>
        <Profile
          alt={name}
          className={styles.profile}
          name={name}
          size="S"
          src={avatar ?? undefined}
          variant="green"
        />
      </Box>
      <Box alignItems="center" className={styles.actions} display="flex">
        {isOwner ? (
          <Box>
            <Pill>{intl.formatMessage({ id: 'common.owner', defaultMessage: 'Owner' })}</Pill>
          </Box>
        ) : (
          <Box className={styles.accessLevel}>
            <Select
              aria-label={intl.formatMessage(
                {
                  id: 'sharing.access_level_aria_label',
                  defaultMessage: 'Access level for {name}'
                },
                { name }
              )}
              disabled={isOwner}
              onChange={event => onAccessLevelChange?.(event.target.value as ExplicitPermissionLevel)}
              value={accessLevel}
              width="100%"
              maxWidth="100%"
              size="small"
              mb={0}
              listMaxHeight={200}
            >
              <Option
                key="none"
                text={intl.formatMessage({ id: 'sharing.none', defaultMessage: 'None' })}
                value="no-access"
              />
              <Option
                key="view"
                text={intl.formatMessage({ id: 'sharing.view_only', defaultMessage: 'View only' })}
                value="view"
              />
              <Option
                key="edit"
                text={intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}
                value="edit"
              />
            </Select>
          </Box>
        )}
        {!isOwner && (
          <Box>
            <Button
              aria-label={intl.formatMessage(
                {
                  id: 'sharing.remove_person_aria_label',
                  defaultMessage: 'Remove {name}'
                },
                { name }
              )}
              buttonType="tertiary"
              className={isOwner ? styles.removeButtonHidden : undefined}
              disabled={isOwner}
              iconType="bin"
              onClick={isOwner ? undefined : onRemove}
              type="button"
              size="small"
              ml={0}
              destructive
            />
          </Box>
        )}
      </Box>
    </li>
  );
};
